import { MappingTabTreeViewSideBar } from "@/components/table-preview/sidebar";
import { useCategoryId, useWorkbookId } from "@/hooks/use-workbook";
import queries from "@/queries";
import { Fact, useMappingStore } from "@/stores/mapping";
import { usePreviewStore } from "@/stores/preview";
import { Dimension } from "@/types/mapping";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useCurrentAnalyzeStatus, useRetryAnalyzeXbrl } from "../sidebar/hooks";
import { isSheetAnalyzing } from "../sidebar/task-status";
import { MappingAxisSelectSection } from "./axis-select";
import { MappingCellDataSection } from "./cell-select";
import ExtendTagSection from "./extend-tag/extend-tag-section";
import { useTableId } from "./hooks";
import {
  AnalyzingState,
  FailedState,
  NotTargetState,
} from "./retrieval-results/analyzing-animation";
import { MappingSearchResultAxisSection } from "./retrieval-results/axis-section";
import { AutoTagSection } from "./retrieval-results/auto-tag-section";
import {
  hasAutoTagRecord,
  useAutoTagStatus,
} from "@/hooks/mutations/use-auto-tag-status";
import { useCellAutoTagInfo } from "./retrieval-results/auto-tag.hooks";
import { MappingSearchResultLineItemsSection } from "./retrieval-results/line-items-section";
import { NoResultActions } from "./retrieval-results/no-result-actions";
import SearchTagSection from "./search-tag/search-tag-section";
import { MappingTitleSection } from "./title";
import { usePreviewIssueCellHighlight } from "./use-preview-issue-cell-highlight";
import { useEditableTreeView } from "../table-preview/table/use-editable-preview";

const MappingViewPanel = () => {
  const dimension = useMappingStore((state) => state.dimension.dimension);
  const [mode, setMode] = useState<"mapping" | "search" | "extend">("mapping");
  const [showPreviewSidebar, setShowPreviewSidebar] = useState(false);
  const [showAutoTag, setShowAutoTag] = useState(false);
  const selectedFact = useMappingStore((state) => state.fact.selected);
  const prevSelectedFact = useRef<Fact | null>(null);
  const prevDimension = useRef<Dimension | null>(null);

  const { setWorkbookId, setTableId } = usePreviewStore((state) => state);
  const { data: workbookId } = useWorkbookId();
  const { data: categoryId } = useCategoryId();
  const {
    data: { tableId },
  } = useTableId();

  // 선택 셀이 오토태깅된 경우에만 헤더에 '오토태깅' 뷰 버튼을 노출한다.
  const { isAutoTagged } = useCellAutoTagInfo();
  const autoTagAvailable = isAutoTagged;

  // 워크북에 오토태깅 작업이 있으면(셀 선택과 무관) 진행 패널/뷰를 노출 — 전체 진행 표시용.
  // 읽기 전용 구독 — 폴링·완료 처리는 페이지 루트의 useAutoTagCompletionEffect 전담.
  const { data: autoTagStatus } = useAutoTagStatus(workbookId ?? undefined);
  const hasAutoTagJob = autoTagStatus?.tables.some(hasAutoTagRecord) ?? false;
  const autoTagViewAvailable = autoTagAvailable || hasAutoTagJob;

  const {
    mutate: retryAnalysis,
    isPending: isRetrying,
    variables: analyzingSheetIds,
  } = useRetryAnalyzeXbrl();
  // 요청이 분석 끝까지 블로킹이라, 시트로 좁히지 않으면 다른 주석으로 이동했을 때
  // 그쪽 버튼까지 "분석 중"으로 잠긴다.
  const isAnalyzingThisSheet =
    isRetrying && !!categoryId && !!analyzingSheetIds?.includes(categoryId);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (workbookId && tableId) {
      setWorkbookId(workbookId);
      setTableId(tableId);
    }
  }, [workbookId, tableId]);

  // 시트(테이블) 변경 시 태그 빌딩 사이드바 닫기
  useEffect(() => {
    setShowPreviewSidebar(false);
  }, [tableId]);

  // 매핑 뷰 진입 시 task status 1회 invalidate하여 최신 분석 상태 반영
  useEffect(() => {
    if (!workbookId) return;
    queryClient.invalidateQueries(
      queries.workbook(workbookId)._ctx.taskStatus(),
    );
  }, [workbookId, queryClient]);

  // TreeView 데이터 fetch (PreviewSideBar용)
  const {
    tableTreeView,
    isLoading: isTableTreeViewLoading,
    error: tableTreeViewError,
  } = useEditableTreeView({
    enabled: showPreviewSidebar,
  });
  const status = useCurrentAnalyzeStatus();
  // 오토태깅은 퍼블릭 — beta 와 무관하게 작업/태깅 기록이 있으면 뷰를 노출한다.
  const canShowAutoTagView = autoTagViewAvailable;

  usePreviewIssueCellHighlight({
    workbookId,
    tableId,
    // 부정 목록이면 모르는 상태(null/undefined·새 상태)가 조회를 켠다. 하이라이트할
    // 매핑 화면이 실제로 그려지는 건 COMPLETED 뿐이다 (나머지는 아래서 early return).
    enabled: status === "COMPLETED",
  });

  useEffect(() => {
    if (selectedFact) {
      const hasChanged =
        selectedFact.address !== prevSelectedFact.current?.address ||
        selectedFact.sheet.id !== prevSelectedFact.current?.sheet.id ||
        selectedFact.table.id !== prevSelectedFact.current?.table.id ||
        dimension !== prevDimension.current;

      if (hasChanged) {
        setMode("mapping");
      }

      prevSelectedFact.current = selectedFact;
      prevDimension.current = dimension;
    }
  }, [selectedFact, dimension]);

  const handlePreviewSidebarToggle = () => {
    setShowPreviewSidebar((prev) => !prev);
    setShowAutoTag(false);
  };

  const handleAutoTagToggle = () => {
    setShowAutoTag((prev) => !prev);
    setShowPreviewSidebar(false);
  };

  if (isSheetAnalyzing(status)) {
    return (
      <div className="w-[500px] h-full">
        <AnalyzingState />
      </div>
    );
  }

  if (status === "FAILED") {
    return (
      <div className="w-[500px] h-full">
        <FailedState
          onRetry={() => categoryId && retryAnalysis([categoryId])}
          disabled={!categoryId}
          pending={isAnalyzingThisSheet}
        />
      </div>
    );
  }

  if (status === "NOT_TARGET") {
    return (
      <div className="w-[500px] h-full">
        <NotTargetState
          onAnalyze={() => categoryId && retryAnalysis([categoryId])}
          disabled={!categoryId}
          pending={isAnalyzingThisSheet}
        />
      </div>
    );
  }

  return (
    <div className="w-[500px] h-full">
      {/* Frame 2087329522 */}
      <div className="flex flex-col w-full h-full">
        {/* Frame 2087329534 - 매출채권 및 기타채권 전체탭 */}
        <MappingTitleSection />
        {/* 셀 속성 - 0.4~14.9 전체 탭 */}
        <MappingCellDataSection />
        {/* ViewSwitchTab - 행/열/트리 전체탭 */}
        <MappingAxisSelectSection
          showPreviewSidebar={showPreviewSidebar}
          onPreviewSidebarClick={handlePreviewSidebarToggle}
          showAutoTag={showAutoTag}
          onAutoTagClick={handleAutoTagToggle}
          autoTagAvailable={canShowAutoTagView}
        />
        {showPreviewSidebar ? (
          <div className="w-full flex-1 overflow-y-auto">
            <MappingTabTreeViewSideBar
              tableTreeView={tableTreeView}
              isLoading={isTableTreeViewLoading}
              error={tableTreeViewError}
            />
          </div>
        ) : showAutoTag && canShowAutoTagView ? (
          <div className="w-full flex-1 overflow-y-auto">
            <AutoTagSection />
          </div>
        ) : (
          <ContentSection mode={mode} dimension={dimension} setMode={setMode} />
        )}
      </div>
    </div>
  );
};

const ContentSection = ({
  mode,
  dimension,
  setMode,
}: {
  mode: "mapping" | "search" | "extend";
  dimension: Dimension;
  setMode: (mode: "mapping" | "search" | "extend") => void;
}) => {
  if (mode === "mapping") {
    return <MappingSection dimension={dimension} setMode={setMode} />;
  } else if (mode === "search") {
    return (
      <SearchTagSection
        className="flex flex-col h-full overflow-y-auto"
        onDeleteButtonClick={() => {
          setMode("mapping");
        }}
      />
    );
  } else if (mode === "extend") {
    return (
      <ExtendTagSection
        dimension={dimension}
        onDeleteButtonClick={() => {
          setMode("mapping");
        }}
      />
    );
  }

  return <></>;
};

const MappingSection = ({
  dimension,
  setMode,
}: {
  dimension: Dimension;
  setMode: (mode: "mapping" | "search" | "extend") => void;
}) => {
  const noResultActions = (
    <NoResultActions
      dimension={dimension}
      onClickSearchButton={() => setMode("search")}
      onClickExtendButton={() => setMode("extend")}
    />
  );

  return (
    <div className="w-full flex-1 overflow-y-auto">
      {dimension === "LINE_ITEM" ? (
        <MappingSearchResultLineItemsSection
          noResultActionComponent={noResultActions}
        />
      ) : dimension === "AXIS" ? (
        <MappingSearchResultAxisSection
          noResultActionComponent={noResultActions}
        />
      ) : null}
    </div>
  );
};

export default MappingViewPanel;
